import { useState, useEffect, useMemo } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { useAuth } from "@/contexts/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import {
  ArrowLeft, User, Phone, Mail, Car, Building2, UserCog, Calendar,
  StickyNote, Clock, Pencil, Save, Eye, History as HistoryIcon, ChevronRight
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Iter 159 — Ficha de Cliente / Vista 360
export default function CustomerProfilePage() {
  const { customer_key } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { token } = useAuth();
  const axiosAuth = () => axios.create({
    baseURL: API,
    headers: { Authorization: `Bearer ${token}` },
  });

  const [profile, setProfile] = useState(null);
  const [timeline, setTimeline] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({ name: "", phone: "", email: "", observations: "", notes: "" });
  const [saving, setSaving] = useState(false);

  // Si entra desde /admin/clientes?phone=...&email=... resolver clave primero
  const resolveAndLoad = async () => {
    setLoading(true); setError("");
    try {
      let key = customer_key;
      if (!key) {
        const phone = searchParams.get("phone") || "";
        const email = searchParams.get("email") || "";
        const customer_id = searchParams.get("customer_id") || "";
        const r = await axiosAuth().get("/customers/lookup", { params: { phone, email, customer_id } });
        key = r.data.key;
        navigate(`/admin/clientes/${encodeURIComponent(key)}`, { replace: true });
        return;  // useEffect re-llamará con la nueva ruta
      }
      const [p, t] = await Promise.all([
        axiosAuth().get(`/customers/${encodeURIComponent(key)}`),
        axiosAuth().get(`/customers/${encodeURIComponent(key)}/timeline`),
      ]);
      setProfile(p.data);
      setTimeline(t.data?.items || []);
      setForm({
        name: p.data?.name || "",
        phone: p.data?.phone || "",
        email: p.data?.email || "",
        observations: p.data?.observations || "",
        notes: p.data?.internal_notes?.notes || "",
      });
    } catch (e) {
      setError(e?.response?.data?.detail || "No se pudo cargar la ficha del cliente");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { resolveAndLoad(); /* eslint-disable-next-line */ }, [customer_key]);

  const canEdit = !!profile?.can_edit;

  const save = async () => {
    if (!canEdit) {
      toast.error("Sin permiso para editar la ficha del cliente");
      return;
    }
    setSaving(true);
    try {
      await axiosAuth().put(`/customers/${encodeURIComponent(customer_key)}`, form);
      toast.success("Ficha actualizada");
      setEditing(false);
      await resolveAndLoad();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error guardando");
    } finally { setSaving(false); }
  };

  if (loading) return <div className="p-6 text-sm text-slate-500">Cargando ficha…</div>;
  if (error) return (
    <div className="p-6">
      <Button variant="ghost" onClick={() => navigate(-1)} size="sm"><ArrowLeft className="w-4 h-4 mr-1" /> Volver</Button>
      <p className="text-red-600 mt-4 text-sm">{error}</p>
    </div>
  );
  if (!profile) return null;

  return (
    <div className="p-6 max-w-7xl mx-auto" data-testid="customer-profile-page">
      <div className="flex items-center gap-2 mb-4">
        <Button variant="ghost" size="sm" onClick={() => navigate("/admin/clientes")} data-testid="customer-back-to-list-btn">
          <ArrowLeft className="w-4 h-4 mr-1" /> Volver a clientes
        </Button>
        {/* Iter 160 — breadcrumb */}
        <nav className="hidden md:flex items-center gap-1.5 text-xs text-slate-500" aria-label="breadcrumb"
          data-testid="customer-profile-breadcrumb">
          <span className="text-slate-400">Clientes</span>
          <ChevronRight className="w-3 h-3 text-slate-300" />
          <span className="font-semibold text-slate-700">Ficha de cliente</span>
        </nav>
        {!canEdit && (
          <span className="ml-auto inline-flex items-center gap-1 px-2 py-1 rounded-sm text-[10px] uppercase tracking-wider font-bold bg-amber-100 text-amber-800 border border-amber-200" data-testid="customer-read-only-badge">
            <Eye className="w-3 h-3" /> Modo solo lectura
          </span>
        )}
      </div>

      {/* Header */}
      <Card className="p-5 mb-5">
        <div className="flex flex-wrap items-start gap-4">
          <div className="w-14 h-14 rounded-sm bg-slate-900 text-white flex items-center justify-center text-2xl font-bold">
            {(profile.name || "?").charAt(0).toUpperCase()}
          </div>
          <div className="flex-1 min-w-0">
            <h1 className="text-xl font-bold text-slate-900" data-testid="customer-name">{profile.name || "—"}</h1>
            <div className="flex flex-wrap gap-3 mt-2 text-xs text-slate-600">
              {profile.phone && <span className="flex items-center gap-1" data-testid="customer-phone"><Phone className="w-3 h-3" /> {profile.phone}</span>}
              {profile.email && <span className="flex items-center gap-1" data-testid="customer-email"><Mail className="w-3 h-3" /> {profile.email}</span>}
              {profile.habitual_center && <span className="flex items-center gap-1"><Building2 className="w-3 h-3" /> {profile.habitual_center}</span>}
              {profile.habitual_advisor && <span className="flex items-center gap-1"><UserCog className="w-3 h-3" /> {profile.habitual_advisor}</span>}
            </div>
            <div className="flex flex-wrap gap-2 mt-3 text-[11px]">
              <span className="px-2 py-0.5 bg-slate-100 rounded-sm border border-slate-200">
                Última visita: <b>{profile.last_visit || "—"}</b>
              </span>
              <span className="px-2 py-0.5 bg-slate-100 rounded-sm border border-slate-200" data-testid="customer-visits-count">
                Visitas: <b>{profile.visits_count}</b>
              </span>
              <span className="px-2 py-0.5 bg-slate-100 rounded-sm border border-slate-200">
                Citas confirmadas: <b>{profile.appointments_summary?.confirmed || 0}</b>
              </span>
              <span className="px-2 py-0.5 bg-slate-100 rounded-sm border border-slate-200">
                Canceladas: <b>{profile.appointments_summary?.cancelled || 0}</b>
              </span>
            </div>
          </div>
          {canEdit && !editing && (
            <Button onClick={() => setEditing(true)} variant="outline" size="sm" data-testid="customer-edit-btn">
              <Pencil className="w-3.5 h-3.5 mr-1" /> Editar ficha
            </Button>
          )}
          {canEdit && editing && (
            <Button onClick={save} size="sm" disabled={saving} data-testid="customer-save-btn">
              <Save className="w-3.5 h-3.5 mr-1" /> {saving ? "Guardando…" : "Guardar"}
            </Button>
          )}
        </div>
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        {/* Columna izquierda: datos editables + vehículos */}
        <div className="space-y-5">
          <Card className="p-4">
            <h2 className="text-xs uppercase tracking-wider font-bold text-slate-500 mb-3 flex items-center gap-1"><User className="w-3 h-3" /> Datos del cliente</h2>
            <div className="space-y-3 text-sm">
              <FieldRow label="Nombre" value={form.name} editing={editing && canEdit}
                onChange={v => setForm(f => ({ ...f, name: v }))} testid="field-name" />
              <FieldRow label="Teléfono" value={form.phone} editing={editing && canEdit}
                onChange={v => setForm(f => ({ ...f, phone: v }))} testid="field-phone" />
              <FieldRow label="Email" value={form.email} editing={editing && canEdit}
                onChange={v => setForm(f => ({ ...f, email: v }))} testid="field-email" />
              <div className="space-y-1">
                <Label className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Observaciones internas</Label>
                {editing && canEdit ? (
                  <Textarea rows={2} value={form.observations} data-testid="field-observations"
                    onChange={e => setForm(f => ({ ...f, observations: e.target.value }))} />
                ) : (
                  <p className="text-xs text-slate-600 whitespace-pre-wrap">{profile.observations || "—"}</p>
                )}
              </div>
            </div>
          </Card>

          <Card className="p-4">
            <h2 className="text-xs uppercase tracking-wider font-bold text-slate-500 mb-3 flex items-center gap-1"><Car className="w-3 h-3" /> Vehículos asociados ({profile.vehicles.length})</h2>
            {profile.vehicles.length === 0 ? (
              <p className="text-xs text-slate-400">Sin vehículos registrados</p>
            ) : (
              <ul className="text-sm space-y-1">
                {profile.vehicles.map(v => (
                  <li key={v.plate} className="flex items-center gap-2 text-slate-700">
                    <span className="font-mono text-xs bg-slate-100 px-1.5 py-0.5 rounded">{v.plate}</span>
                    <span className="text-slate-600">{[v.brand, v.model].filter(Boolean).join(" ") || "—"}</span>
                  </li>
                ))}
              </ul>
            )}
          </Card>
        </div>

        {/* Columna central: notas internas */}
        <div className="space-y-5">
          <Card className="p-4">
            <h2 className="text-xs uppercase tracking-wider font-bold text-slate-500 mb-3 flex items-center gap-1"><StickyNote className="w-3 h-3" /> Notas internas del cliente</h2>
            {editing && canEdit ? (
              <Textarea rows={6} value={form.notes} data-testid="field-internal-notes"
                onChange={e => setForm(f => ({ ...f, notes: e.target.value }))}
                placeholder="Solo visibles para el equipo. Nunca se envían al cliente." />
            ) : (
              <p className="text-sm text-slate-700 whitespace-pre-wrap" data-testid="customer-notes-display">
                {profile.internal_notes?.notes || "Sin notas internas"}
              </p>
            )}
            {profile.internal_notes?.updated_at && (
              <p className="text-[10px] text-slate-400 mt-2">
                Última edición: {profile.internal_notes.updated_at.slice(0, 16).replace("T", " ")} · {profile.internal_notes.updated_by || "—"}
              </p>
            )}
          </Card>
        </div>

        {/* Columna derecha: timeline */}
        <div className="space-y-5">
          <Card className="p-4">
            <h2 className="text-xs uppercase tracking-wider font-bold text-slate-500 mb-3 flex items-center gap-1"><HistoryIcon className="w-3 h-3" /> Timeline de interacciones</h2>
            {timeline.length === 0 ? (
              <p className="text-xs text-slate-400" data-testid="customer-timeline-empty">Sin interacciones registradas</p>
            ) : (
              <ul className="space-y-3" data-testid="customer-timeline">
                {timeline.map((ev, idx) => (
                  <li key={idx} className="flex gap-2 text-xs" data-testid={`timeline-event-${ev.kind}`}>
                    <div className="flex flex-col items-center pt-0.5">
                      <div className={`w-2 h-2 rounded-full ${kindColor(ev.kind)}`}></div>
                      {idx < timeline.length - 1 && <div className="w-px flex-1 bg-slate-200 mt-1"></div>}
                    </div>
                    <div className="flex-1 pb-2">
                      <p className="text-[10px] text-slate-400 font-mono">{(ev.when || "").slice(0, 16).replace("T", " ")}</p>
                      <p className="text-xs font-semibold text-slate-800">{ev.title}</p>
                      {ev.summary && <p className="text-xs text-slate-500 mt-0.5">{ev.summary}</p>}
                      <div className="flex gap-1 mt-1">
                        {ev.source && <span className="text-[9px] uppercase tracking-wider bg-slate-100 text-slate-600 px-1 rounded">{ev.source}</span>}
                        {ev.user_name && <span className="text-[9px] text-slate-400">· {ev.user_name}</span>}
                      </div>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </Card>
        </div>
      </div>
    </div>
  );
}

function FieldRow({ label, value, editing, onChange, testid }) {
  return (
    <div className="space-y-1">
      <Label className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">{label}</Label>
      {editing ? (
        <Input value={value} onChange={(e) => onChange(e.target.value)} className="h-8 text-sm" data-testid={testid} />
      ) : (
        <p className="text-sm text-slate-700" data-testid={`${testid}-display`}>{value || "—"}</p>
      )}
    </div>
  );
}

function kindColor(kind) {
  if (kind === "email_sent") return "bg-blue-500";
  if (kind === "email_failed") return "bg-red-500";
  if (kind === "appointment") return "bg-slate-500";
  if (kind === "internal_note_updated") return "bg-amber-500";
  if (kind === "rescheduled") return "bg-purple-500";
  if (kind === "cancelled") return "bg-red-500";
  if (kind === "confirmed") return "bg-emerald-500";
  return "bg-slate-400";
}
